/**
 * Logic for the post-purchase revision page.
 *
 * Deliberately DOM-free so it can be unit-tested with `node --test`; the wiring lives in
 * assets/revision-page-ui.js. Same UMD shim as assets/image-upload-safety.js - a plain
 * script in the theme, a module under node, no build step.
 *
 * One rule shapes most of this file: the browser sends colour *labels*, never prompt
 * text, and never an order id. The exact phrases the generator needs live server-side in
 * TellMyTale-Klaviyo-Integration-API/utils/personalizationPrompts.js, and the order is
 * looked up there from the client_id. Nothing here can point a revision at another order.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.RevisionPage = api;
})(typeof self !== 'undefined' ? self : this, function () {
  // Swatch colours are illustrative only - they tint the SVG in the picker and are never
  // sent anywhere. The labels, however, must match HAIR_LABELS/EYE_LABELS on the server
  // exactly: an unrecognised one is rejected there rather than reaching the generator.
  var HAIR_OPTIONS = [
    { label: 'Blonde', color: '#e8c97a' },
    { label: 'Black', color: '#1a1a1a' },
    { label: 'Brown', color: '#6b4423' },
    { label: 'Red', color: '#a0522d' },
    { label: 'White', color: '#e6e2da' }
  ];

  var EYE_OPTIONS = [
    { label: 'Brown', color: '#5b3a1e' },
    { label: 'Blue', color: '#4a7fb5' },
    { label: 'Green', color: '#4e7d4e' },
    { label: 'Hazel', color: '#8e7037' }
  ];

  function normalizeName(value) {
    if (typeof value !== 'string') return '';
    return value.trim().replace(/\s+/g, ' ');
  }

  function nameChanged(name, currentName) {
    var next = normalizeName(name);
    return Boolean(next) && next !== normalizeName(currentName);
  }

  function buildBookPayload(selection) {
    var payload = { client_id: selection.clientId };
    if (selection.hair) payload.hair = selection.hair;
    if (selection.eyes) payload.eyes = selection.eyes;
    if (nameChanged(selection.name, selection.currentName)) {
      payload.name = normalizeName(selection.name);
    }
    return payload;
  }

  function buildPagePayload(selection) {
    var payload = { client_id: selection.clientId, page_number: selection.pageNumber };
    if (selection.hair) payload.hair = selection.hair;
    if (selection.eyes) payload.eyes = selection.eyes;
    return payload;
  }

  /**
   * Reads back as a phrase, e.g. "red hair and blue eyes". Empty when nothing was picked, which
   * is a normal choice now: a customer can ask for a page to be drawn again without changing
   * anything. Callers building a sentence should use describeIntent instead so they do not end
   * up with a gap where the colours would have been.
   */
  function describeSelection(selection) {
    selection = selection || {};
    var parts = [];
    if (selection.hair) parts.push(selection.hair.toLowerCase() + ' hair');
    if (selection.eyes) parts.push(selection.eyes.toLowerCase() + ' eyes');
    return parts.join(' and ');
  }

  /** What we are about to do, phrased for a confirmation line, with or without a selection. */
  function describeIntent(selection) {
    var summary = describeSelection(selection);
    return summary ? 'with ' + summary : 'with the same colors';
  }

  /**
   * 1-based page numbers whose URL changed between two polls of preview-images.
   * A different length means the whole book was replaced, which is handled by loading
   * the new client id rather than badging individual cards.
   */
  function diffPageUrls(previous, next) {
    if (!Array.isArray(previous) || !Array.isArray(next)) return [];
    if (previous.length !== next.length) return [];

    var changed = [];
    for (var i = 0; i < next.length; i++) {
      if (previous[i] !== next[i]) changed.push(i + 1);
    }
    return changed;
  }

  /**
   * True for a page that has no child on it: the dedication, the back cover and anything
   * else the generator ships unchanged for every customer.
   *
   * The generator names them for us. A personalized page is `12.jpg`; a static one is
   * `12_static.jpg`. Offering "Fix this page" on those is worse than useless: the request
   * succeeds, the card says "Updated!", one of the customer's three changes is spent, and
   * the artwork comes back byte-for-byte identical because there is nothing on it to
   * redraw.
   */
  function isStaticPage(url) {
    if (typeof url !== 'string') return false;
    var path = url.split('?')[0];
    var file = path.substring(path.lastIndexOf('/') + 1);
    return /_static\.[a-z0-9]+$/i.test(file);
  }

  return {
    HAIR_OPTIONS: HAIR_OPTIONS,
    EYE_OPTIONS: EYE_OPTIONS,
    normalizeName: normalizeName,
    buildBookPayload: buildBookPayload,
    buildPagePayload: buildPagePayload,
    describeSelection: describeSelection,
    describeIntent: describeIntent,
    diffPageUrls: diffPageUrls,
    isStaticPage: isStaticPage
  };
});
